import { differenceInMinutes, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { gettext } from '@eventbrite/i18n';

import { TimeSlotProps, TimeSlotSignalProps } from '@eventbrite/timed-entry';
import React from 'react';
import {
    Discount,
    DiscountType,
    SalesStatus,
    TimeSlot,
    UrgencySignal,
} from '../../../state/infiniteCalendarSeries';
import { DiscountSvg } from '../assests/DiscountSvg';
import { DayPart, TAB_HEADERS } from '../types';
import { urgencySignals } from './signalConstants';

export function getSessionDayPart(
    startDatetime: string,
    timezone: string,
): keyof DayPart {
    const startTime = utcToZonedTime(startDatetime, timezone);
    const startHour = startTime.getHours();
    if (startHour >= 4 && startHour < 12) return 'Morning';
    if (startHour >= 12 && startHour < 18) return 'Afternoon';
    return 'Night';
}

export function getUrgencySignals(session: TimeSlot) {
    if (session.salesStatus !== SalesStatus.onSale) {
        return [];
    }
    const displaySignals = Object.entries(session.urgencySignals).reduce(
        (signals, [key, value]) => {
            if (value) {
                const urgency = urgencySignals[key as keyof UrgencySignal];
                signals.push({
                    className: urgency.bgColor,
                    icon: urgency.icon,
                    text: urgency.text,
                });
            }
            return signals;
        },
        [] as TimeSlotSignalProps[],
    );
    if (session.discount.discountValue > 0) {
        displaySignals.push({
            className: 'discount',
            icon: <DiscountSvg />,
            text: '',
        });
    }
    return displaySignals;
}
export const formatDiscountText = (discount: Discount): string =>
    `${discount.discountValue}${
        discount.discountType === DiscountType.percentage ? '%' : ''
    } ${gettext('off')}`;

export function getOverallDiscount(timeSlots: Array<TimeSlot>) {
    const overallDiscount = timeSlots.find(
        (item) => item.discount.discountValue > 0,
    );
    return overallDiscount
        ? formatDiscountText(overallDiscount.discount)
        : undefined;
}

export function getSessionDuration(startUtc: string, endUtc: string) {
    const startDate = parseISO(startUtc);
    const endDate = parseISO(endUtc);
    return differenceInMinutes(endDate, startDate);
}

export function getDurationString(minutes: number) {
    if (minutes < 60) {
        return `${minutes} ${gettext('mins')}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    return remainingMinutes === 0
        ? `${hours} ${hours === 1 ? gettext('hr') : gettext('hrs')}`
        : `${(minutes / 60).toFixed(1)} ${gettext('hrs')}`;
}

export function getSessionsByDurationAndTimeOfDay(
    timeSlots: TimeSlot[],
    timezone: string,
    locale: string,
) {
    const CATEGORIES = ['Morning', 'Afternoon', 'Night'] as const;
    type Category = typeof CATEGORIES[number];
    const createEmptyCategoryGroup = (): Record<Category, TimeSlotProps[]> => ({
        Morning: [],
        Afternoon: [],
        Night: [],
    });
    return timeSlots.reduce(
        (
            acc: {
                sessionsByDuration: Record<string, TimeSlotProps[]>;
                sessionsByDurationAndTimeOfDay: Record<
                    string,
                    Record<Category, TimeSlotProps[]>
                >;
            },
            session,
        ) => {
            const duration = getSessionDuration(
                session.startDatetime,
                session.endDatetime,
            );
            const category = getSessionDayPart(
                session.startDatetime,
                timezone,
            ) as Category;
            const urgencySignals = getUrgencySignals(session);
            //TODO: This is commented out because of type error in TimeSlotProps, this will be taken care in https://eventbrite.atlassian.net/browse/EB-297101
            // const isSoldOut = session.salesStatus === SalesStatus.soldOut;
            const isDisable = session.salesStatus === SalesStatus.notOnSale;
            const props: TimeSlotProps = {
                id: session.id,
                startDatetime: session.startDatetime,
                // isSoldOut,
                urgencySignals,
                timezone,
                locale,
                isDisable,
            };
            acc.sessionsByDuration[duration] ??= [];
            acc.sessionsByDurationAndTimeOfDay[duration] ??=
                createEmptyCategoryGroup();
            acc.sessionsByDuration[duration].push(props);
            acc.sessionsByDurationAndTimeOfDay[duration][category].push(props);
            return acc;
        },
        {
            sessionsByDuration: {},
            sessionsByDurationAndTimeOfDay: {},
        },
    );
}

export const hasTimeSlots = (
    sessionsByDurationAndTimeOfDay: Record<
        string,
        Record<string, TimeSlotProps[]>
    >,
) =>
    Object.values(sessionsByDurationAndTimeOfDay).some((sessions) =>
        Object.values(sessions).some((timeSlots) => timeSlots.length > 0),
    );

export const getSortedSessionsByDuration = (
    sessionsByDuration: Record<string, TimeSlotProps[]>,
): { duration: number; sessions: TimeSlotProps[] }[] =>
    Object.entries(sessionsByDuration)
        .map(([duration, sessions]) => ({
            duration: Number(duration),
            sessions: sessions.sort(
                (a, b) =>
                    new Date(a.startDatetime).getTime() -
                    new Date(b.startDatetime).getTime(),
            ),
        }))
        .filter(({ duration }) => !isNaN(duration) && duration >= 0)
        .sort((a, b) => a.duration - b.duration);

export const getDefaultActiveTab = (
    sessionsByDurationAndTime: Record<string, Record<string, TimeSlotProps[]>>,
): string | null => {
    for (const duration of Object.keys(sessionsByDurationAndTime)) {
        const foundTab = TAB_HEADERS.find(
            (tab) =>
                Object.keys(sessionsByDurationAndTime[duration]).includes(
                    tab,
                ) && sessionsByDurationAndTime[duration][tab]?.length > 0,
        );
        if (foundTab) return foundTab;
    }
    return null;
};

export function getSortedSessionsByDurationAndTimeOfDay(
    sessionsByDurationAndTimeOfDay: Record<
        string,
        Record<string, TimeSlotProps[]>
    >,
) {
    return Object.entries(sessionsByDurationAndTimeOfDay).sort(
        ([a], [b]) => Number(a) - Number(b),
    );
}

export const getFormattedTime = (
    isoString: string,
    locale: string,
    timeZone: string,
): string => {
    if (!isoString || isNaN(Date.parse(isoString))) {
        return '';
    }
    const date = new Date(isoString);
    return date.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone,
    });
};
